import { api, getAllPages } from 'api';
import { isBefore, parse, startOfDay, subDays } from 'date-fns';
import type { CreateOrder } from 'types/CreateOrder';
import type { CreateOrderPosition } from 'types/CreateOrderPosition';
import type { DeliveryDetails } from 'types/DeliveryDetails';
import type { FinalDeliveryType } from 'types/FinalDeliveryType';
import type { InternalOrderNote } from 'types/InternalOrderNote';
import type { List } from 'types/List';
import type { Nullable } from 'types/Nullable';
import type { Order } from 'types/Order';
import type { OrderAddress } from 'types/OrderAddress';
import type { OrderList } from 'types/OrderList';
import type { OrderListElement } from 'types/OrderListElement';
import type { OrderModification } from 'types/OrderModification';
import type { OrderModificationReason } from 'types/OrderModificationReason';
import type { OrderPriceAdjustments } from 'types/OrderPriceAdjustments';
import type { OrderStatus } from 'types/OrderStatus';
import type { OrderType } from 'types/OrderType';
import type { OrderValidationResult } from 'types/OrderValidationResult';
import type { ParcelCheckIn } from 'types/ParcelCheckIn';
import type { ReassignDeliveryPartnerReason } from 'types/ReassignDeliveryPartnerReason';
import type { UpdateOrderPosition } from 'types/UpdateOrderPosition';
import type { ValidateOrder } from 'types/ValidateOrder';
import { SEARCH_ORDERS_DATE_FORMAT_FROM, SEARCH_ORDERS_MAX_DATE_RANGE, Url } from 'utils/constants';
import { format } from 'utils/date';

export const punchNewOrder = ({ orderBody }: { orderBody: CreateOrder }): Promise<void> =>
  api.post(`${Url.KITCHEN_MANAGEMENT_API_URL}/orders/create`, orderBody);

interface GetOrdersOptions {
  signal?: AbortSignal;
  isFetchingAllPages?: boolean;
  orderNumber?: string;
  orderStatuses?: OrderStatus[];
  orderTypes?: OrderType[];
  finalDeliveryType?: FinalDeliveryType | null;
  deliveryPartnerId?: string | null;
  kitchenId?: string | null;
  regionId?: string | null;
  pointsOfSaleId?: string[];
  phoneNumber?: string | null;
  brandId?: string | null;
  ordersIds?: string[];
  businessId?: string | null;
  dateFrom: string;
  dateTo: string;
  page?: number;
  perPage?: number;
  sort?: string;
  sortOrder?: string;
  customerId?: string | null;
}

export const getOrders = ({
  signal,
  isFetchingAllPages,
  orderNumber,
  orderStatuses = [],
  orderTypes = [],
  finalDeliveryType,
  deliveryPartnerId,
  kitchenId,
  regionId,
  pointsOfSaleId = [],
  phoneNumber,
  brandId,
  ordersIds = [],
  businessId,
  dateFrom,
  dateTo,
  page,
  perPage,
  sort,
  sortOrder,
  customerId,
}: GetOrdersOptions): Promise<OrderList<OrderListElement>> => {
  const minAllowedDateFrom = startOfDay(subDays(new Date(), SEARCH_ORDERS_MAX_DATE_RANGE));
  const parsedDateFrom = parse(dateFrom, SEARCH_ORDERS_DATE_FORMAT_FROM, new Date());

  const safeDateFrom = isBefore(startOfDay(parsedDateFrom), minAllowedDateFrom)
    ? format(minAllowedDateFrom, SEARCH_ORDERS_DATE_FORMAT_FROM)
    : dateFrom;

  const url = `${Url.ORDER_READ_API_URL}/orders`;
  const config = {
    params: {
      page,
      perPage,
      sort,
      orderType: orderTypes,
      finalDeliveryType,
      deliveryPartnerId,
      kitchenId,
      regionId,
      pointOfSaleId: pointsOfSaleId,
      brandId,
      businessId,
      sortOrder,
      customerPhoneNumber: phoneNumber,
      number: orderNumber,
      orderStatus: orderStatuses,
      orderId: ordersIds,
      createdDateTimeFrom: safeDateFrom,
      createdDateTimeTo: dateTo,
      customerId,
    },
    signal,
  };

  // axios 1.20 widened `get`, so the two methods no longer share a callable signature
  return isFetchingAllPages ? getAllPages(url, config) : api.get(url, config);
};

export const cancelOrder = ({
  posID,
  orderID,
  cancelReason,
  isScheduledForFullRefund,
}: {
  posID: string;
  orderID: string;
  cancelReason: string;
  isScheduledForFullRefund?: boolean;
}): Promise<void> =>
  api.delete(`${Url.KITCHEN_MANAGEMENT_API_URL}/point-of-sales/${posID}/orders/${orderID}`, {
    data: {
      reason: cancelReason,
      isScheduledForFullRefund,
    },
  });

export const getOrder = ({ orderID }: { orderID: string }): Promise<Order> =>
  api.get(`${Url.KITCHEN_MANAGEMENT_API_URL}/orders/${orderID}`);

export const getDeliveryDetails = ({ orderID }: { orderID: string }): Promise<DeliveryDetails> =>
  api.get(`${Url.DELIVERY_HUB_API_URL}/deliveries/${orderID}`, {
    hasErrorDialog: false,
  });

export const reassignDeliveryPartner = ({
  deliveryPartnerInRegionId,
  reason,
  orderId: deliveryId,
}: {
  deliveryPartnerInRegionId: string;
  orderId: string;
  reason: ReassignDeliveryPartnerReason;
}): Promise<void> =>
  api.post(`${Url.DELIVERY_HUB_API_URL}/deliveries/${deliveryId}/reassign`, {
    deliveryPartnerInRegionId,
    reason,
  });

export const assignDeliveryPartner = ({
  deliveryPartnerInRegionId,
  orderId: parcelId,
}: {
  deliveryPartnerInRegionId: string;
  orderId: string;
}): Promise<void> =>
  api.put(`${Url.DELIVERY_HUB_API_URL}/parcels/${parcelId}/preferred-dp`, {
    deliveryPartnerInRegionId,
  });

export const closeDelivery = ({ deliveryId }: { deliveryId: string }): Promise<void> =>
  api.put(`${Url.DELIVERY_HUB_API_URL}/deliveries/${deliveryId}/close`, undefined, {
    hasErrorDialog: false,
  });

export const getParcelCheckInTime = ({
  orderId: parcelId,
}: {
  orderId: string;
}): Promise<ParcelCheckIn> =>
  api.get(`${Url.DELIVERY_HUB_API_URL}/parcels/${parcelId}/check-in`, { hasErrorDialog: false });

export const validateOrder = (
  { posID, orderBody }: { posID: string | undefined; orderBody: ValidateOrder },
  { signal, hasErrorDialog = true }: { signal?: AbortSignal; hasErrorDialog?: boolean } = {}
): Promise<OrderValidationResult> =>
  api.post(`${Url.KITCHEN_MANAGEMENT_API_URL}/point-of-sales/${posID}/orders/validate`, orderBody, {
    hasErrorDialog,
    signal,
  });

export const getPotentialDuplicates = ({
  pointOfSaleId,
  sourceOrderFriendlyId,
}: {
  pointOfSaleId: string;
  sourceOrderFriendlyId: string;
}): Promise<List<OrderListElement>> =>
  api.get(`${Url.KITCHEN_MANAGEMENT_API_URL}/orders/duplicates`, {
    params: { pointOfSaleId, sourceOrderFriendlyId },
  });

export const updateOrderNote = ({
  orderID,
  note,
}: {
  orderID: string;
  note: string;
}): Promise<void> => api.put(`${Url.KITCHEN_MANAGEMENT_API_URL}/orders/${orderID}/notes`, { note });

export const updateOrderAddress = ({
  orderID,
  address,
}: {
  orderID: string;
  address: Omit<OrderAddress, 'coordinates' | 'customAddressLabel'>;
}): Promise<void> =>
  api.put(`${Url.KITCHEN_MANAGEMENT_API_URL}/orders/${orderID}/address`, address);

export const updateOrderPositionNote = ({
  orderId,
  positionId,
  note,
}: {
  orderId: string;
  positionId: string;
  note: string;
}): Promise<void> =>
  api.put(`${Url.KITCHEN_MANAGEMENT_API_URL}/orders/${orderId}/positions/${positionId}/notes`, {
    note,
  });

interface UpdateOrder {
  orderId: string;
  orderPositionsToAdd: CreateOrderPosition[];
  orderPositionsToUpdate: UpdateOrderPosition[];
  orderPositionsToDelete: string[];
  orderDiscountId?: string | null;
  orderDeliveryDiscountId?: string;
  modificationReason?: OrderModificationReason;
  priceAdjustments?: OrderPriceAdjustments;
  walletPaymentAmount: Nullable<number>;
}

export const updateOrder = ({
  orderId,
  orderPositionsToAdd,
  orderPositionsToUpdate,
  orderPositionsToDelete,
  orderDiscountId,
  orderDeliveryDiscountId,
  modificationReason,
  priceAdjustments,
  walletPaymentAmount,
}: UpdateOrder): Promise<void> =>
  api.put(`${Url.KITCHEN_MANAGEMENT_API_URL}/orders/${orderId}/positions`, {
    orderPositionsToAdd,
    orderPositionsToUpdate,
    orderPositionsToDelete,
    orderDiscountId,
    orderDeliveryDiscountId,
    modificationReason,
    priceAdjustments,
    walletPaymentAmount,
  });

export const assignAgent = ({ orderId }: { orderId: string }): Promise<void> =>
  api.post(`${Url.KITCHEN_MANAGEMENT_API_URL}/orders/${orderId}/customer-contacts`);

export const getOrderModifications = ({
  orderId,
}: {
  orderId: string;
}): Promise<List<OrderModification>> =>
  api.get(`${Url.KITCHEN_MANAGEMENT_API_URL}/orders/${orderId}/updates`);

export const getInternalOrderNotes = ({
  orderId,
}: {
  orderId: string;
}): Promise<List<InternalOrderNote>> =>
  api.get(`${Url.KITCHEN_MANAGEMENT_API_URL}/orders/${orderId}/internal-notes`);

export const addInternalOrderNote = ({
  orderId,
  note,
}: {
  orderId: string;
  note: string;
}): Promise<void> =>
  api.post(`${Url.KITCHEN_MANAGEMENT_API_URL}/orders/${orderId}/internal-notes`, {
    note,
  });

export const resolveInternalOrderNote = ({
  orderId,
  noteId,
}: {
  orderId: string;
  noteId: string;
}): Promise<void> =>
  api.put(`${Url.KITCHEN_MANAGEMENT_API_URL}/orders/${orderId}/internal-notes/${noteId}`);

export const deleteInternalOrderNote = ({
  orderId,
  noteId,
}: {
  orderId: string;
  noteId: string;
}): Promise<void> =>
  api.delete(`${Url.KITCHEN_MANAGEMENT_API_URL}/orders/${orderId}/internal-notes/${noteId}`);
